import type { DashboardApplication } from "@cv-screener/shared";

export type ApplicationSortKey = "candidate" | "job" | "score" | "confidence" | "status";
export type ApplicationSortDirection = "ascending" | "descending";

export type ApplicationSort = {
  key: ApplicationSortKey;
  direction: ApplicationSortDirection;
};

const confidenceRank: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

export function defaultApplicationSortDirection(key: ApplicationSortKey): ApplicationSortDirection {
  return key === "score" ? "descending" : "ascending";
}

export function updateApplicationSorts(current: ApplicationSort[], key: ApplicationSortKey, makePrimary: boolean): ApplicationSort[] {
  if (makePrimary) return [{ key, direction: defaultApplicationSortDirection(key) }];
  const existingIndex = current.findIndex((sort) => sort.key === key);
  if (existingIndex < 0) return [...current, { key, direction: defaultApplicationSortDirection(key) }];
  const existing = current[existingIndex]!;
  if (existing.direction !== defaultApplicationSortDirection(key)) return current.filter((_, index) => index !== existingIndex);
  return current.map((sort, index) => index === existingIndex
    ? { ...sort, direction: sort.direction === "descending" ? "ascending" : "descending" }
    : sort);
}

function applicationSortValue(application: DashboardApplication, key: ApplicationSortKey): string | number {
  if (key === "candidate") return application.candidateName.toLocaleLowerCase();
  if (key === "job") return application.jobId.toLocaleLowerCase();
  if (key === "score") return application.overallScore;
  if (key === "confidence") return confidenceRank[application.confidence] ?? 0;
  return application.status.toLocaleLowerCase();
}

export function sortApplications(applications: DashboardApplication[], sorts: ApplicationSort[]): DashboardApplication[] {
  return applications.map((application, index) => ({ application, index })).sort((first, second) => {
    for (const sort of sorts) {
      const left = applicationSortValue(first.application, sort.key);
      const right = applicationSortValue(second.application, sort.key);
      const comparison = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      if (comparison !== 0) return sort.direction === "descending" ? -comparison : comparison;
    }
    return first.index - second.index;
  }).map(({ application }) => application);
}
